import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, OnInit, ViewChild, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs/internal/operators/finalize';
import { PageSize } from 'src/app/enumeration/pageSize';
import { LmsApiService } from 'src/app/service/lms/lms-api-service';

@Component({
  selector: 'app-lms-course-catalog',
  imports: [
    CommonModule,
    NgxPaginationModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './lms-course-catalog.html',
  styleUrl: './lms-course-catalog.css',
})
export class LmsCourseCatalog implements OnInit {

  @ViewChild('editInputFocus') editInputEl!: ElementRef<HTMLInputElement>;

  private readonly apiService = inject(LmsApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly courseCatalogList = signal<any[]>([]);
  readonly total = signal<number>(0);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1);
  readonly pageSize = signal<number>(PageSize.DEFAULT_LIST);

  readonly apiLoading = signal<boolean>(false);
  readonly loading = signal<boolean>(false);
  readonly errorMessage = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);

  catalogName = signal<string>('');
  editingId = signal<number | null>(null);
  originalNameBackup = signal<string>('');

  ngOnInit(): void {
    this.fetchCatalogRecords();
  }

  fetchCatalogRecords() {
    this.loading.set(true);

    const zeroBasedPage = this.page() - 1;

    this.apiService.getLMSCourseCatalogList(zeroBasedPage)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.loading.set(false)))
      .subscribe({
        next: (v: any) => {
          this.courseCatalogList.set(v?.Message.lmsCourseCatalogInfoRespLst || []);
          this.total.set(v?.Message.pageTotal || 0);
          this.allTotal.set(v?.Message.allTotal || 0);
        },
        error: (err: any) => {
          this.showError(err || 'Network error occurred while fetching catalog records.');
        }
      });
  }

  createCatalog(): void {

    this.apiLoading.set(true);

    this.apiService.getCreateLMSCourseCatalog(this.catalogName())
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: () => {
          this.showSuccess('Successfully created new catalog entry.');
          this.catalogName.set('');
          this.fetchCatalogRecords();
          this.closeModalViaNativeElement('createCatalogModal');
        },
        error: (error: any) => {
          this.showError(error || 'A network error occurred while updating the items list.');
          this.closeModalViaNativeElement('addNewCatalog');
        }
      });

  }

  toggleCatalogStatus(catalog: any) {
    this.apiLoading.set(true);

    this.apiService.getEnabledDisabledLMSCourseCatalog(catalog.name, !catalog.enabled)
      .pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.apiLoading.set(false)))
      .subscribe({
        next: () => {
          this.showSuccess('Successfully updated catalog status.');
          this.fetchCatalogRecords();
        },
        error: (error: any) => {
          this.showError(error || 'A network error occurred while updating the catalog status.');
        }
      });
  }

  startInlineEdit(catalog: any): void {
    this.editingId.set(catalog.id);
    this.originalNameBackup.set(catalog.name);

    setTimeout(() => {
      if (this.editInputEl) {
        this.editInputEl.nativeElement.focus();
        this.editInputEl.nativeElement.select();
      }
    }, 50);
  }

  cancelInlineEdit(): void {
    const currentEditingId = this.editingId();
    if (currentEditingId) {
      const currentCatalog = this.courseCatalogList().find(c => c.id === currentEditingId);
      if (currentCatalog) {
        currentCatalog.name = this.originalNameBackup();
      }
    }
    this.editingId.set(null);
  }


  submitInlineUpdate(catalog: any): void {
    if (!catalog.name || !catalog.name.trim()) {
      this.showError('Catalog name cannot be empty.');
      return;
    }

    if (this.originalNameBackup().trim() === catalog.name.trim()) {
      this.editingId.set(null);
      return;
    }

    this.apiLoading.set(true);

    this.apiService.getUpdateLMSCourseCatalog(catalog.id, catalog.name.trim())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.apiLoading.set(false))
      )
      .subscribe({
        next: () => {
          this.showSuccess('Successfully updated catalog name.');
          this.editingId.set(null);
          this.fetchCatalogRecords();
        },
        error: (error: any) => {
          this.showError(error || 'A network error occurred while updating the catalog name.');
          this.cancelInlineEdit();
        }
      });
  }

  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);
      this.fetchCatalogRecords();
    }
  }

  // Message Display Utilities Helpers
  showError(msg: string): void {
    this.errorMessage.set(msg);
    // Auto clear error banner after 6 seconds
    setTimeout(() => this.clearAlerts(), 6000);
  }

  showSuccess(msg: string): void {
    this.successMessage.set(msg);
    // Auto clear success banner after 4 seconds
    setTimeout(() => this.clearAlerts(), 4000);
  }

  clearAlerts(): void {
    this.errorMessage.set(null);
    this.successMessage.set(null);
  }

  goBack(): void {
    window.history.back();
  }

  private closeModalViaNativeElement(elementId: string): void {
    const targetModalNode = document.getElementById(elementId);
    if (targetModalNode) {
      const bootstrapInstance = (window as any).bootstrap?.Modal?.getInstance(targetModalNode);
      bootstrapInstance?.hide();
    }
  }
}
